/**
 * Each PHP app's installed version against the security floor of its PHP major,
 * as last recorded by the QuickBox CLI.
 *
 * Endpoint: GET /api/admin/qb/php-security (admin only, plain GET, no CSRF).
 * `checkedAt: null` means the CLI has not recorded a result yet, which is never
 * presented as secure. An empty `pools` list with a `checkedAt` means no PHP app
 * is installed.
 */
import { useCallback, useEffect, useRef, useState } from "react";

// Local mirror of `PhpSecurityStatus` in `src/domain/system/phpSecurityStatus.ts`;
// the web tsconfig does not reach backend modules.
export type PhpSecurityPool = {
  app: string;
  major: string;
  /** Installed PHP package version, or null when it could not be read. */
  installed: string | null;
  floor: string;
  ok: boolean;
};

export type PhpSecurityStatus = {
  checkedAt: string | null;
  pools: PhpSecurityPool[];
};

export type PhpPoolState = "ok" | "below" | "unreadable";

/** A pool whose version could not be read is reported apart from one known to be below. */
export const phpPoolState = (pool: PhpSecurityPool): PhpPoolState =>
  pool.ok ? "ok" : pool.installed === null ? "unreadable" : "below";

export const hasPhpBelowFloor = (status: PhpSecurityStatus | null): boolean =>
  status?.pools.some((pool) => phpPoolState(pool) === "below") ?? false;

const isStatus = (value: unknown): value is PhpSecurityStatus => {
  if (!value || typeof value !== "object") return false;
  const { checkedAt, pools } = value as Record<string, unknown>;
  return (checkedAt === null || typeof checkedAt === "string") && Array.isArray(pools);
};

export const usePhpSecurityStatus = ({ enabled }: { enabled: boolean }) => {
  const [data, setData] = useState<PhpSecurityStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const refetch = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const res = await fetch("/api/admin/qb/php-security", { credentials: "include", signal: controller.signal });
      const body = (await res.json()) as { ok?: unknown; data?: unknown };
      if (!res.ok || body.ok !== true || !isStatus(body.data)) throw new Error("php security status unavailable");
      setData(body.data);
      setFailed(false);
    } catch {
      if (controller.signal.aborted) return;
      setData(null);
      setFailed(true);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refetch();
    return () => abortRef.current?.abort();
  }, [enabled, refetch]);

  return { data, loading, failed, refetch };
};
